import { Entity, Guid } from '@ebeihilfe/core';

import { LegacyFeldTyp } from '../enums';
import { LegacyAntragsMetadata } from './legacy-antrags-metadata';

/**
 * Represents the metadata associated with an Antrag that will be evaluated by the Backend.
 */
export class AntragMetaData extends Entity<AntragMetadataProps> {
  constructor(props: AntragMetadataProps, id?: string) {
    super(props, id);
  }

  public get formResults(): FormResult[] {
    return this.getProps().formResults;
  }
}

export class FormResult extends Entity<FormResultsProps | LegacyFormResultsProps> {
  constructor(props: FormResultsProps, id?: string) {
    super(props, id);
  }

  public get templateName(): string {
    return this.getProps().templateName;
  }

  public get formId(): Guid {
    return this.getProps().formId;
  }

  public get createdAt(): Date {
    return this.getProps().createdAt;
  }

  public get isValid(): boolean {
    return this.getProps().isValid;
  }

  public get isMandatory(): boolean {
    return this.getProps().isMandatory;
  }

  public get results(): FormValue[] {
    return this.getProps().results;
  }
}

interface AntragMetadataProps {
  formResults: FormResult[];
}

interface FormResultsProps {
  templateName: string;
  formId: Guid;
  createdAt: Date;
  isValid: boolean;
  isMandatory: boolean;
  results: FormValue[];
}

interface LegacyFormResultsProps extends FormResultsProps {
  templateName: '::LEGACY::';
  results: FormValue[];
}

export class FormValue extends Entity<ResultProps> {
  private constructor(props: ResultProps, id?: string) {
    super(props, id);
  }

  public get name(): string {
    return this.getProps().name;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get value(): any {
    return this.getProps().value;
  }

  public get $type(): string {
    return this.getProps().$type;
  }

  public get parent(): string | undefined {
    return this.getProps().parent;
  }

  public static createFromLegacy(name: string, legacyType: LegacyFeldTyp, value: string | boolean): FormValue {
    if (legacyType === LegacyFeldTyp.CHECKBOX || typeof value === 'boolean') {
      return new FormValue({
        $type: 'checkbox',
        name,
        value: !!value,
      });
    } else {
      return new FormValue({
        $type: 'text',
        name,
        value: value ?? '',
      });
    }
  }

  public static createLegacy(name: string, type: string, value: string | boolean): LegacyAntragsMetadata {
    if (type === 'checkbox' || typeof value === 'boolean') {
      return {
        keyDom: '',
        type: LegacyFeldTyp.CHECKBOX,
        keyInternal: name,
        value,
      };
    } else {
      return {
        keyDom: '',
        type: LegacyFeldTyp.TEXT,
        keyInternal: name,
        value,
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static create(type: string, name: string, value: any, parent?: string): FormValue {
    return new FormValue({
      $type: type,
      name: name,
      value: value,
      parent: parent,
    });
  }
}

type ResultProps = GenericResultProps;

interface GenericResultProps {
  $type: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  parent?: string;
}
